import React, { useState } from 'react';
import './RestaurantPopup.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaperPlane,
  faHeart,
  faBookmark,
  faArrowLeft
} from '@fortawesome/free-solid-svg-icons';

const RestaurantPopup = ({
  restaurant,
  currentCity,
  onClose,
  onTrackButtonClick,
  getCurrentStatus,
  getCurrentHours,
  getRestaurantPhoto,
  getRestaurantPhotos,
  generateAppleMapsLink
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (!restaurant) return null;

  console.log('🖼️ RestaurantPopup - Restaurant data:', restaurant.name);
  console.log('🖼️ RestaurantPopup - Has liveData?', !!restaurant.liveData);
  console.log('🖼️ RestaurantPopup - liveData.photos:', restaurant.liveData?.photos);

  const status = getCurrentStatus(restaurant);
  const currentHours = getCurrentHours(restaurant);

  // Get all photos from Google Places API
  const allPhotos = getRestaurantPhotos ? getRestaurantPhotos(restaurant) : [];
  const fallbackPhoto = getRestaurantPhoto(restaurant);

  console.log('🖼️ RestaurantPopup - allPhotos:', allPhotos);
  console.log('🖼️ RestaurantPopup - fallbackPhoto:', fallbackPhoto);

  // Create images array - use all photos if available, otherwise fallback to single photo
  const images = allPhotos.length > 0 ? allPhotos : (fallbackPhoto ? [fallbackPhoto] : []);

  console.log('🖼️ RestaurantPopup - final images array:', images);

  const appleMapsUrl = generateAppleMapsLink(restaurant.name, restaurant.address);

  const editorialDescription = restaurant.description ||
    `A carefully curated dining destination that embodies the sophisticated culinary landscape of ${currentCity.name}. This establishment represents the intersection of exceptional cuisine, thoughtful design, and memorable hospitality.`;

  const handleShare = async () => {
    const shareData = {
      title: `${restaurant.name} in ${currentCity.name}`,
      text: `Found this amazing restaurant: ${restaurant.name} in ${currentCity.name}!`,
      url: `${window.location.origin}/guides?restaurant=${encodeURIComponent(restaurant.name)}`
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        onTrackButtonClick(restaurant.id, 'share', restaurant.name, currentCity.name);
      } else {
        await navigator.clipboard.writeText(shareData.url);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Share error:', error);
      }
    }
  };

  const handlePrevImage = () => {
    setCurrentImageIndex(prev => prev > 0 ? prev - 1 : images.length - 1);
  };

  const handleNextImage = () => {
    setCurrentImageIndex(prev => prev < images.length - 1 ? prev + 1 : 0);
  };

  const handleDotClick = (index) => {
    setCurrentImageIndex(index);
  };

  return (
    <div className="restaurant-popup-overlay" onClick={onClose}>
      <div className="restaurant-popup-content" onClick={(e) => e.stopPropagation()}>
        {/* Mobile Back Button */}
        <button className="popup-close-button" onClick={onClose}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>

        <div className="restaurant-popup-inner">

          {/* Header Section */}
          <div className="popup-header">
            <div className="popup-header-actions">
              <button
                className="popup-action-icon"
                onClick={handleShare}
                title="Share"
              >
                <FontAwesomeIcon icon={faPaperPlane} style={{ color: '#666' }} />
              </button>
              <button
                className="popup-action-icon"
                onClick={() => onTrackButtonClick(restaurant.id, 'like', restaurant.name, currentCity.name)}
                title="Like"
              >
                <FontAwesomeIcon icon={faHeart} style={{ color: '#666' }} />
              </button>
              <button
                className="popup-action-icon"
                onClick={() => onTrackButtonClick(restaurant.id, 'save', restaurant.name, currentCity.name)}
                title="Save"
              >
                <FontAwesomeIcon icon={faBookmark} style={{ color: '#666' }} />
              </button>
            </div>

            <div className="popup-subtitle">
              {currentCity?.name || 'Location'} Dining
            </div>

            <h1 className="popup-title">{restaurant.name}</h1>
          </div>

          {/* Main Content Layout */}
          <div className="popup-content-grid">

            {/* Left Column: Image */}
            <div className="popup-image-section">
              {images.length > 0 ? (
                <div className="popup-carousel">
                  {images.map((img, index) => {
                    console.log(`🖼️ Image ${index}:`, img, 'active:', index === currentImageIndex);
                    return (
                      <img
                        key={index}
                        className={`carousel-image ${index === currentImageIndex ? 'active' : ''}`}
                        src={img}
                        alt={`${restaurant.name} - Photo ${index + 1}`}
                        onError={(e) => {
                          console.error('❌ Failed to load image:', img);
                          console.error('Error details:', e);
                        }}
                        onLoad={() => console.log('✅ Successfully loaded image:', img)}
                      />
                    );
                  })}

                  {images.length > 1 && (
                    <>
                      <div className="carousel-dots">
                        {images.map((_, index) => (
                          <button
                            key={index}
                            className={`carousel-dot ${index === currentImageIndex ? 'active' : ''}`}
                            onClick={() => handleDotClick(index)}
                          />
                        ))}
                      </div>

                      <button className="carousel-prev" onClick={handlePrevImage}>‹</button>
                      <button className="carousel-next" onClick={handleNextImage}>›</button>
                    </>
                  )}
                </div>
              ) : (
                <div className="popup-image-placeholder">
                  <div className="placeholder-icon">🍽️</div>
                  <div className="placeholder-text">Photography coming soon</div>
                </div>
              )}

              <div className="popup-image-caption">
                {restaurant.type || 'Restaurant'} • {restaurant.priceRange || '$'}
                {restaurant.liveData?.rating && ` • ★ ${restaurant.liveData.rating}`}
              </div>
            </div>

            {/* Right Column: Content */}
            <div className="popup-info-section">

              {/* Status Indicator */}
              {status.isOpen !== null && (
                <div className="popup-status">
                  <div
                    className="status-dot"
                    style={{
                      background: status.isOpen ? '#2ECC71' : status.isOpen === false ? '#E74C3C' : '#3498DB'
                    }}
                  />
                  <span>
                    {status.isOpen === true ? 'Currently Open' : status.isOpen === false ? 'Currently Closed' : 'Hours Available'}
                    {status.source === 'live' ? ' • Live Data' : ' • Estimated'}
                  </span>
                </div>
              )}

              {/* Description */}
              <div className="popup-description">
                <p>{editorialDescription}</p>

                {restaurant.features && Array.isArray(restaurant.features) && restaurant.features.length > 0 && (
                  <p className="popup-features">
                    Notable features include {restaurant.features.slice(0, 3).join(', ').toLowerCase()},
                    creating an atmosphere that speaks to both sophistication and comfort.
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="popup-actions">
                <div className="popup-primary-actions">
                  <button
                    className="popup-btn-primary"
                    onClick={() => {
                      onTrackButtonClick(restaurant.id, 'open_maps', restaurant.name, currentCity.name);
                      window.open(appleMapsUrl);
                    }}
                  >
                    Reservations • Directions • Delivery
                  </button>
                </div>

                <div className="popup-secondary-actions">
                  {restaurant.liveData?.formatted_phone_number || restaurant.phone ? (
                    <button
                      className="popup-btn-tertiary"
                      onClick={() => {
                        onTrackButtonClick(restaurant.id, 'call', restaurant.name, currentCity.name);
                        window.open(`tel:${restaurant.liveData?.formatted_phone_number || restaurant.phone}`);
                      }}
                    >
                      Call Restaurant
                    </button>
                  ) : null}
                </div>
              </div>

            </div>
          </div>

          {/* Bottom Details */}
          <div className="popup-details">
            <div className="popup-details-grid">
              <div className="popup-detail-item">
                <div className="popup-detail-label">Location</div>
                <div className="popup-detail-value">{restaurant.address}</div>
              </div>

              {(restaurant.liveData?.formatted_phone_number || restaurant.phone) && (
                <div className="popup-detail-item">
                  <div className="popup-detail-label">Contact</div>
                  <a
                    href={`tel:${restaurant.liveData?.formatted_phone_number || restaurant.phone}`}
                    className="popup-detail-value popup-detail-link"
                  >
                    {restaurant.liveData?.formatted_phone_number || restaurant.phone}
                  </a>
                </div>
              )}

              {currentHours && (
                <div className="popup-detail-item">
                  <div className="popup-detail-label">Hours</div>
                  <div className="popup-detail-value">{currentHours}</div>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default RestaurantPopup;
